/**
 * Shared API Error Handling - Core (synchronous only)
 *
 * This file intentionally contains NO async functions so that mobile's Jest
 * environment can import it without needing @babel/runtime helpers (which are
 * only accessible from mobile/node_modules, not from the root shared/ path).
 *
 * Consumers:
 *   - shared/errors/index.ts       re-exports everything + adds withRetry (async)
 *   - shared/errors/apiErrorPresentation.ts  uses parseError for the adapter API
 *   - mobile/src/services/apiErrorPresentation.ts  imports via apiErrorPresentation.ts
 *
 * @see shared/errors/index.ts for the full module (including withRetry)
 */

// --- Error Classification ----------------------------------------------------

export type ErrorKind =
  | 'auth'            // 401/403, expired tokens, insufficient permissions
  | 'validation'      // 400, field-level validation failures
  | 'not_found'       // 404, resource does not exist
  | 'conflict'        // 409, duplicate or state conflict
  | 'rate_limit'      // 429, too many requests
  | 'network'         // No connectivity, timeout, DNS failure
  | 'server'          // 500+, backend crashed
  | 'not_configured'  // Feature not enabled for this tenant/env
  | 'quota'           // Usage limits exceeded
  | 'circuit_breaker' // Service circuit breaker tripped (repeated 5xx failures)
  | 'unknown';        // Unclassified

export interface ErrorPresentation {
  /** User-safe message to display */
  friendlyMessage: string;
  /** Error classification for routing (show login, show retry, etc.) */
  kind: ErrorKind;
  /** HTTP status code if applicable */
  status?: number;
  /** Backend or transport error code */
  code?: string;
  /** Whether this error is retryable */
  retryable: boolean;
  /** Field-level errors for form validation */
  fieldErrors?: Record<string, string[]>;
}

// --- Error Classification Logic ----------------------------------------------

/** Classify an HTTP status code into a canonical ErrorKind. */
export function classifyHttpStatus(status: number): ErrorKind {
  if (status === 401 || status === 403) return 'auth';
  if (status === 400) return 'validation';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limit';
  if (status >= 500) return 'server';
  return 'unknown';
}

/** Returns true for error kinds where a retry is appropriate (network, server, rate-limit). */
export function isRetryable(kind: ErrorKind): boolean {
  return kind === 'network' || kind === 'server' || kind === 'rate_limit';
}

// --- Default Messages per Kind -----------------------------------------------

const DEFAULT_MESSAGES: Record<ErrorKind, string> = {
  auth: 'Your session has expired. Please sign in again.',
  validation: 'Please check the form and correct any errors.',
  not_found: 'The requested resource was not found.',
  conflict: 'This action conflicts with the current state. Please refresh and try again.',
  rate_limit: 'Too many requests. Please wait a moment and try again.',
  network: 'Unable to reach the server. Please check your connection.',
  server: 'The server is temporarily unavailable. Please try again shortly.',
  not_configured: 'This feature is not configured for your environment.',
  quota: 'Usage limit exceeded. Please contact support.',
  circuit_breaker: 'The service is temporarily unavailable due to recent failures. Please try again shortly.',
  unknown: 'Something went wrong. Please try again.',
};

/** Returns the default user-facing message for a given ErrorKind. */
export function getDefaultMessage(kind: ErrorKind): string {
  return DEFAULT_MESSAGES[kind];
}

// --- Error Parsing -----------------------------------------------------------

interface RawErrorShape {
  response?: {
    status?: number;
    data?: unknown;
  };
  code?: string;
  message?: string;
}

function extractFieldErrors(data: unknown): Record<string, string[]> | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const record = data as Record<string, unknown>;
  const fieldErrors: Record<string, string[]> = {};
  let hasFields = false;

  for (const [key, value] of Object.entries(record)) {
    if (['detail', 'message', 'error', 'code', 'error_code', 'non_field_errors'].includes(key)) continue;
    if (Array.isArray(value) && value.every(v => typeof v === 'string')) {
      fieldErrors[key] = value;
      hasFields = true;
    }
  }

  return hasFields ? fieldErrors : undefined;
}

function extractDetailMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const record = data as Record<string, unknown>;

  for (const key of ['detail', 'message', 'error', 'non_field_errors']) {
    const val = record[key];
    if (typeof val === 'string' && val.trim()) return val;
    if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') return val.join(' ');
  }

  // Fall back to the first field-level error array (e.g. { username: ["Already taken."] })
  for (const [key, value] of Object.entries(record)) {
    if (['detail', 'message', 'error', 'code', 'error_code', 'non_field_errors'].includes(key)) continue;
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') return value[0];
  }

  return undefined;
}

function extractBackendCode(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const record = data as Record<string, unknown>;
  const code = record.code ?? record.error_code;
  return typeof code === 'string' ? code : undefined;
}

// --- Known Backend Error Codes -----------------------------------------------

const KNOWN_CODES: Record<string, { message: string; kind: ErrorKind }> = {
  'AI_NOT_CONFIGURED': { message: 'AI is not enabled. Contact an administrator.', kind: 'not_configured' },
  'EMAIL_SEND_NOT_CONFIGURED': { message: 'Email sending is not configured.', kind: 'not_configured' },
  'EMAIL_SEND_QUOTA_EXCEEDED': { message: 'Email quota exceeded. Try again later.', kind: 'quota' },
  'EMAIL_SEND_UNAUTHORIZED': { message: 'Email auth failed. Reconnect your account.', kind: 'auth' },
  'EMAIL_SEND_FAILED': { message: 'Email could not be sent. Try again.', kind: 'server' },
  'TENANT_NOT_FOUND': { message: 'Organization not found.', kind: 'not_found' },
  'TENANT_INACTIVE': { message: 'This organization is inactive.', kind: 'auth' },
  'TRIAL_EXPIRED': { message: 'Your trial has expired. Please upgrade.', kind: 'quota' },
  'DUPLICATE_ENTRY': { message: 'This record already exists.', kind: 'conflict' },
};

// --- Main Parser -------------------------------------------------------------

/**
 * Parse any thrown error into a standardized ErrorPresentation.
 * Works with Axios errors, fetch errors, and plain Error objects.
 */
export function parseError(error: unknown, fallback?: string): ErrorPresentation {
  // Fast path: duck-type check for ApiError/ApiServiceError instances.
  // These carry parsed metadata directly on the error so we can skip re-parsing.
  if (
    error instanceof Error &&
    'friendlyMessage' in error &&
    'kind' in error &&
    'retryable' in error
  ) {
    const apiErr = error as {
      friendlyMessage: string;
      kind: ErrorKind;
      status?: number;
      code?: string;
      retryable: boolean;
      fieldErrors?: Record<string, string[]>;
    };
    return {
      friendlyMessage: apiErr.friendlyMessage,
      kind: apiErr.kind,
      status: apiErr.status,
      code: apiErr.code,
      retryable: apiErr.retryable,
      fieldErrors: apiErr.fieldErrors,
    };
  }

  const raw = (error ?? null) as RawErrorShape | null;
  const status = raw?.response?.status;
  const transportCode = raw?.code;
  const data = raw?.response?.data;
  const backendCode = extractBackendCode(data);

  if (backendCode && KNOWN_CODES[backendCode]) {
    const known = KNOWN_CODES[backendCode];
    return {
      friendlyMessage: known.message,
      kind: known.kind,
      status,
      code: backendCode,
      retryable: isRetryable(known.kind),
      fieldErrors: extractFieldErrors(data),
    };
  }

  if (transportCode === 'ERR_NETWORK' || transportCode === 'ECONNABORTED') {
    return {
      friendlyMessage: getDefaultMessage('network'),
      kind: 'network',
      code: transportCode,
      retryable: true,
    };
  }

  if (typeof status === 'number') {
    const kind = classifyHttpStatus(status);
    // 401 errors always use the canonical "session expired" message to avoid
    // leaking backend internals and to provide consistent user guidance.
    // 403 (permission denied) can still use the backend's message.
    const backendMessage = status === 401 ? undefined : extractDetailMessage(data);
    return {
      friendlyMessage: backendMessage || getDefaultMessage(kind),
      kind,
      status,
      code: backendCode || transportCode,
      retryable: isRetryable(kind),
      fieldErrors: kind === 'validation' ? extractFieldErrors(data) : undefined,
    };
  }

  const detailMessage = extractDetailMessage(data);
  if (detailMessage) {
    return {
      friendlyMessage: detailMessage,
      kind: 'unknown',
      status,
      code: backendCode || transportCode,
      retryable: false,
      fieldErrors: extractFieldErrors(data),
    };
  }

  const rawMessage = raw?.message;
  return {
    friendlyMessage: (rawMessage && !/request failed with status/i.test(rawMessage)) ? rawMessage : (fallback || getDefaultMessage('unknown')),
    kind: 'unknown',
    retryable: false,
  };
}

// --- ApiError Base Class --------------------------------------------------------

/**
 * Base Error class that wraps an ErrorPresentation.
 * Extend this to create service-specific typed errors (e.g., ApiServiceError).
 *
 * Properties mirror ErrorPresentation so callers can access `kind`, `status`,
 * `friendlyMessage`, etc. directly on the error instance.
 */
export class ApiError extends Error {
  kind: ErrorKind;
  friendlyMessage: string;
  status?: number;
  code?: string;
  retryable: boolean;
  fieldErrors?: Record<string, string[]>;
  responseData?: unknown;

  constructor(presentation: ErrorPresentation, responseData?: unknown) {
    super(presentation.friendlyMessage);
    this.name = 'ApiError';
    this.kind = presentation.kind;
    this.friendlyMessage = presentation.friendlyMessage;
    this.status = presentation.status;
    this.code = presentation.code;
    this.retryable = presentation.retryable;
    this.fieldErrors = presentation.fieldErrors;
    this.responseData = responseData;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// --- ApiErrorPresentation Adapters -------------------------------------------

/**
 * Presentation-layer view of a parsed API error.
 * Compatible with the legacy mobile apiErrorPresentation.ts API.
 */
export interface ApiErrorPresentation {
  /** User-safe message to display in the UI */
  friendlyMessage: string;
  /** HTTP status code, if applicable */
  status?: number;
  /** Backend or transport error code */
  code?: string;
  /** Error category for routing (show login prompt, retry button, etc.) */
  kind?: ErrorKind;
}

/**
 * Parse any thrown error into a presentation-layer ApiErrorPresentation.
 * Delegates to parseError() for consistent classification.
 */
export function getApiErrorPresentation(
  error: unknown,
  opts?: { fallbackMessage?: string }
): ApiErrorPresentation {
  const parsed = parseError(error, opts?.fallbackMessage);
  return {
    friendlyMessage: parsed.friendlyMessage,
    status: parsed.status,
    code: parsed.code,
    kind: parsed.kind,
  };
}

/**
 * Extract a user-facing error message string from any thrown value.
 * Shorthand for getApiErrorPresentation(error, opts).friendlyMessage.
 */
export function toApiErrorText(
  error: unknown,
  opts?: { fallbackMessage?: string }
): string {
  return getApiErrorPresentation(error, opts).friendlyMessage;
}
